import { useAppDispatch, useAppSelector } from '~/state';
import { setAvailability, selectAvailability } from '~/state/searchFilters';
import { AvailabilityFilters } from '~/utilities/constants';
import { trackEvent } from '~/hooks';
import { AvailabilityOption } from '~/utilities';
import { PrimaryButton } from '@janeapp/burrito-design-system';
import { Check } from '@janeapp/burrito-design-system/icons';
import cn from 'classnames';
import { setLocationManuallySelected } from '~/state/mapData';

const OPTIONS: AvailabilityOption[] = [
  { label: 'All', value: AvailabilityFilters.All },
  { label: 'Today', value: AvailabilityFilters.Today },
  { label: 'Tomorrow', value: AvailabilityFilters.Tomorrow },
  {
    label: 'Within a week',
    value: AvailabilityFilters.WithinAWeek,
  },
];

export type AvailabilityPillProps = {
  option: AvailabilityOption;
  isSelected: boolean;
  onClick: (value: AvailabilityFilters) => void;
};

export function AvailabilityPill({
  option,
  isSelected,
  onClick,
}: AvailabilityPillProps) {
  return (
    <PrimaryButton
      className={cn(
        'border-gray-300 flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1 [text-shadow:none]',
        { 'bg-brand-70 text-white': isSelected },
        { 'bg-neutral-30 text-neutral-100 hover:text-white': !isSelected }
      )}
      onClick={() => onClick(option.value)}
    >
      {isSelected && <Check aria-hidden />}
      {option.label}
    </PrimaryButton>
  );
}

export type AvailabilitySelectPillsProps = {
  className?: string | undefined;
};

export function AvailabilitySelectPills({
  className,
}: AvailabilitySelectPillsProps) {
  const dispatch = useAppDispatch();
  const currentAvailabilityFilter = useAppSelector(selectAvailability);

  const handleClick = (value: AvailabilityFilters) => {
    dispatch(setAvailability(value));
    dispatch(setLocationManuallySelected(false));
    trackEvent('mpl_availability_filter_selected', {
      availability: value,
    });
  };

  return (
    <div
      className={cn(
        'flex flex-nowrap gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className
      )}
      role="group"
      aria-label="Availability filter"
    >
      {OPTIONS.map((option) => (
        <AvailabilityPill
          key={option.value}
          option={option}
          isSelected={currentAvailabilityFilter === option.value}
          onClick={handleClick}
        />
      ))}
    </div>
  );
}
